import { FC } from 'react'
import { Avatar, TextField } from '@mui/material'
import { AuthorizedFeature } from 'components'
import { format, parseISO } from 'date-fns'
import { StrongTypographyStyled } from 'styles'
import { FlexBoxStyled } from 'utility/box'
import { formatWithSubscriptionStatusMask } from 'utility/formatWithSubscriptionStatusMask'
import { MMM_DD_YYYY } from 'utility/timeFormats'
import { FeatureGrant, useCurrentUserQuery } from 'types'
import { getAddressText } from './helpers/getAddressText'
import { ProfileGridStyled } from './styles'

interface IProfileForm {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  sex: string
  address?: string
  sensorCount: number
  sensorModel: string
  sensorSerialNumber: string
  sensorReplacementCount: number
  dietitianSubscriptionStatus: string
  coach: string
  coreSubscriptionPlanName: string
  lastAppVersion: string
  lastAppPlatform: string
  currentCommitment: number
  organizationName: string
  dexcomAccessToken: string
  dexcomUserId: string
  dexcomConnectionCreatedAt: string
  dexcomConnectionType: string
  role: string
}

const RenderForm: FC<{ values: IProfileForm }> = ({ values }) => (
  <form>
    <ProfileGridStyled>
      <TextField label="Email" value={values.email} disabled />
      <TextField label="First Name" value={values.firstName} disabled />
      <TextField label="Last Name" value={values.lastName} disabled />
      <TextField label="Phone Number" value={values.phoneNumber} disabled />
      <TextField label="Sex" value={values.sex} disabled />
      <TextField label="Address" value={values.address} disabled multiline />
      <TextField label="Sensor Count" value={values.sensorCount} disabled />
      <TextField label="Active Sensor Type" value={values.sensorModel} disabled />
      <TextField label="Sensor Serial Number" value={values.sensorSerialNumber} disabled />
      <TextField label="Sensor Replacement Count" value={values.sensorReplacementCount} disabled />
      <TextField label="Organization" value={values.organizationName} disabled />
      <TextField label="App Version" value={values.lastAppVersion} disabled />
      <TextField label="Operating System" value={values.lastAppPlatform} disabled />
      <AuthorizedFeature feature={FeatureGrant.UsersViewFulfillmentConfiguration}>
        <TextField
          label="Nutritionist Subscription Status"
          value={formatWithSubscriptionStatusMask(values.dietitianSubscriptionStatus)}
          disabled
        />
        <TextField
          label="Core Subscription Plan Name"
          value={values.coreSubscriptionPlanName}
          disabled
        />
        <TextField
          label="Current Commitment"
          value={`${values.currentCommitment} month${(values.currentCommitment > 1 && 's') || ''}`}
          disabled
        />
        <TextField label="Role" value={values.role} disabled />
      </AuthorizedFeature>
      <AuthorizedFeature feature={FeatureGrant.DexcomConnectionsViewAccountData}>
        <TextField label="Dexcom Connection Type" value={values.dexcomConnectionType} disabled />
        <TextField label="Dexcom Access Token" value={values.dexcomAccessToken} disabled />
        <TextField label="Dexcom User ID" value={values.dexcomUserId} disabled />
        <TextField
          label="Dexcom Connection Created At"
          value={values.dexcomConnectionCreatedAt}
          disabled
        />
      </AuthorizedFeature>
    </ProfileGridStyled>
  </form>
)

export const Profile = () => {
  const { data } = useCurrentUserQuery()
  const user = data?.currentUser
  const avatar = user?.avatar || ''
  const dob = user?.dateOfBirth

  const sensorReplacementCount = user?.sensorInfo?.replacementRequestsCount
  const coreSubscriptionPlanName = user?.ongoingCoreSubscription?.plans[0]?.nickname

  if (!user) {
    return null
  }
  const userAddress = user.address ? { address: getAddressText(user.address) } : {}

  const initialValues = {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phoneNumber: user.phoneNumber || '',
    sex: user.sex || '',
    ...userAddress,
    sensorCount: user?.sensorInfo?.totalCount || 0,
    sensorModel: user?.sensor?.model || '',
    sensorSerialNumber: user?.sensor?.serialNumber || '',
    sensorReplacementCount: sensorReplacementCount || 0,
    dietitianSubscriptionStatus: formatWithSubscriptionStatusMask(
      user?.lastDietitianSubscription?.stripeStatus
    ),
    coach: user?.lastAsyncChatAssignment?.coach?.fullName || '',
    coreSubscriptionPlanName: coreSubscriptionPlanName || '',
    lastAppVersion: user?.lastAppVersion || '',
    lastAppPlatform: user?.lastAppPlatform || '',
    currentCommitment: user?.ongoingDietitianSubscription?.paidMonthsCount || 0,
    organizationName: user?.organization?.name || '',
    dexcomAccessToken:
      user?.dexcomSteloConnection?.accessToken || user?.dexcomConnection?.accessToken || 'N/A',
    dexcomUserId:
      user?.dexcomSteloConnection?.dexcomUserId || user?.dexcomConnection?.dexcomUserId || 'N/A',
    dexcomConnectionCreatedAt:
      user?.dexcomSteloConnection?.createdAt || user?.dexcomConnection?.createdAt || 'N/A',
    dexcomConnectionType: user?.dexcomSteloConnection
      ? 'Stelo'
      : user?.dexcomConnection
      ? 'Research'
      : 'N/A',
    role: user?.role || ''
  }

  return (
    <FlexBoxStyled flexDirection="column" alignItems="center">
      <Avatar sx={{ width: 96, height: 96, mb: 2 }} src={avatar} alt={user.fullName} />
      {dob && (
        <StrongTypographyStyled variant="subtitle2" mb={2}>{`DOB: ${format(
          parseISO(dob),
          MMM_DD_YYYY
        )}`}</StrongTypographyStyled>
      )}
      <RenderForm values={initialValues} />
    </FlexBoxStyled>
  )
}
